/**
 * Date of birth helpers + personal insights, all pure and unit tested (no network).
 * The "on this day in history" data is fetched separately via the Worker.
 *
 * House style: no em dashes or en dashes anywhere.
 */

export interface Dob {
  d: number; // 1..31
  m: number; // 1..12
  y?: number; // optional year
}

export const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export function daysInMonth(m: number, y?: number): number {
  // Use a leap-safe year when none given so Feb always offers 29.
  return new Date(y ?? 2000, m, 0).getDate();
}

export function isValidDob(dob: Partial<Dob> | null | undefined): dob is Dob {
  if (!dob || typeof dob.d !== "number" || typeof dob.m !== "number") return false;
  if (dob.m < 1 || dob.m > 12) return false;
  if (dob.d < 1 || dob.d > daysInMonth(dob.m, dob.y)) return false;
  if (dob.y != null && (dob.y < 1900 || dob.y > new Date().getFullYear())) return false;
  return true;
}

export function readDob(raw: unknown): Dob | null {
  if (raw && typeof raw === "object") {
    const o = raw as Record<string, unknown>;
    const dob = { d: Number(o.d), m: Number(o.m), y: o.y != null ? Number(o.y) : undefined };
    if (isValidDob(dob)) return dob;
  }
  return null;
}

interface Sign { sign: string; emoji: string; trait: string }

const ZODIAC: { until: [number, number]; info: Sign }[] = [
  { until: [1, 19], info: { sign: "Capricorn", emoji: "♑", trait: "patient and ambitious" } },
  { until: [2, 18], info: { sign: "Aquarius", emoji: "♒", trait: "original and independent" } },
  { until: [3, 20], info: { sign: "Pisces", emoji: "♓", trait: "creative and kind" } },
  { until: [4, 19], info: { sign: "Aries", emoji: "♈", trait: "bold and driven" } },
  { until: [5, 20], info: { sign: "Taurus", emoji: "♉", trait: "steady and grounded" } },
  { until: [6, 20], info: { sign: "Gemini", emoji: "♊", trait: "curious and quick" } },
  { until: [7, 22], info: { sign: "Cancer", emoji: "♋", trait: "caring and intuitive" } },
  { until: [8, 22], info: { sign: "Leo", emoji: "♌", trait: "warm and confident" } },
  { until: [9, 22], info: { sign: "Virgo", emoji: "♍", trait: "precise and thoughtful" } },
  { until: [10, 22], info: { sign: "Libra", emoji: "♎", trait: "fair and charming" } },
  { until: [11, 21], info: { sign: "Scorpio", emoji: "♏", trait: "intense and loyal" } },
  { until: [12, 21], info: { sign: "Sagittarius", emoji: "♐", trait: "adventurous and open" } },
];
const CAPRICORN: Sign = ZODIAC[0].info;

export function zodiac(m: number, d: number): Sign {
  for (const z of ZODIAC) {
    const [zm, zd] = z.until;
    if (m === zm && d <= zd) return z.info;
    if (m < zm) return z.info;
  }
  return CAPRICORN; // Dec 22..31 wraps to Capricorn
}

const BIRTHSTONES = [
  "Garnet", "Amethyst", "Aquamarine", "Diamond", "Emerald", "Pearl",
  "Ruby", "Peridot", "Sapphire", "Opal", "Topaz", "Turquoise",
];
export function birthstone(m: number): string {
  return BIRTHSTONES[(m - 1 + 12) % 12];
}

/** Weekday the reader was born on, or null when the year is unknown. */
export function weekdayBorn(dob: Dob): string | null {
  if (dob.y == null) return null;
  return WEEKDAYS[new Date(dob.y, dob.m - 1, dob.d).getDay()];
}

/** Age on `today`, or null when the year is unknown. */
export function ageOn(dob: Dob, today: Date): number | null {
  if (dob.y == null) return null;
  let age = today.getFullYear() - dob.y;
  const beforeBirthday =
    today.getMonth() + 1 < dob.m || (today.getMonth() + 1 === dob.m && today.getDate() < dob.d);
  if (beforeBirthday) age -= 1;
  return age < 0 ? 0 : age;
}

export function isBirthdayToday(dob: Dob, today: Date): boolean {
  return today.getMonth() + 1 === dob.m && today.getDate() === dob.d;
}

export function daysUntilBirthday(dob: Dob, today: Date): number {
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  let next = new Date(start.getFullYear(), dob.m - 1, dob.d);
  if (next.getTime() < start.getTime()) next = new Date(start.getFullYear() + 1, dob.m - 1, dob.d);
  return Math.round((next.getTime() - start.getTime()) / 86_400_000);
}

export const INSPIRATIONS = [
  "Small steps every day add up to big change.",
  "Your curiosity is a superpower, so keep feeding it.",
  "The best time to start was yesterday. The next best time is now.",
  "Progress beats perfection, every single day.",
  "You are allowed to grow at your own pace.",
  "Be the person you needed when you were younger.",
  "Knowledge compounds. A little each day goes a long way.",
  "Courage is quiet. It just shows up and tries again.",
  "Make today one percent better than yesterday.",
  "Your story is still being written, and you hold the pen.",
  "Stay curious, stay kind, stay you.",
  "Big journeys begin with a single honest step.",
  "What you focus on grows, so choose it well.",
  "You have made it through every hard day so far. Keep that streak.",
  "Learn something today that your future self will thank you for.",
  "Doubt ends more dreams than failure ever will, so begin.",
  "The world needs your particular kind of bright.",
  "Rest is part of the work, not a break from it.",
  "Plant the tree whose shade you may never sit in.",
  "Be brave enough to be a beginner again.",
];

/** Stable-per-person inspiration; pass a seed (for example dob.d + dob.m) to vary it. */
export function pickInspiration(seed: number): string {
  const i = ((Math.floor(seed) % INSPIRATIONS.length) + INSPIRATIONS.length) % INSPIRATIONS.length;
  return INSPIRATIONS[i];
}

/** Day of the year (1..366) for seeding a daily-rotating pick. */
export function dayOfYear(date: Date): number {
  const start = new Date(date.getFullYear(), 0, 0);
  return Math.floor((date.getTime() - start.getTime()) / 86_400_000);
}
